// frontend\src\contexts\AuthContext.js
import { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import api from "../api/api";
import { toast } from "react-toastify";
import Loader from "../components/Loader";

const AuthContext = createContext({
  isAuthenticated: false,
  setAuth: () => {},
  loginUser: async () => {},
  logoutUser: async () => {},
  authTokens: null,
  loading: true,
  username: null,
  first_name: null,
  last_name: null,
  setUsername: () => {},
  isSuperuser: false,
  updateUserData: () => {},
});

const getAuthTokens = () => {
  try {
    const storedTokens = localStorage.getItem("access_token");
    return storedTokens ? JSON.parse(storedTokens) : null;
  } catch (error) {
    console.error("Error parsing authTokens from localStorage:", error);
    localStorage.removeItem("access_token");
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setAuth] = useState(
    !!localStorage.getItem("access_token")
  );
  const [loading, setLoading] = useState(true);
  const [authTokens, setAuthTokens] = useState(getAuthTokens);
  const [username, setUsername] = useState(
    localStorage.getItem("username") || null
  );
  const [first_name, setFirstName] = useState(
    localStorage.getItem("first_name") || null
  );
  const [last_name, setLastName] = useState(
    localStorage.getItem("last_name") || null
  );
  const [isSuperuser, setIsSuperuser] = useState(
    localStorage.getItem("isSuperuser") === "true"
  );

  const navigate = useNavigate();

  const updateUserData = useCallback((data) => {
    if (data.username) {
      setUsername(data.username);
      localStorage.setItem("username", data.username);
    }
    if (data.first_name) {
      setFirstName(data.first_name);
      localStorage.setItem("first_name", data.first_name);
    }
    if (data.last_name) {
      setLastName(data.last_name);
      localStorage.setItem("last_name", data.last_name);
    }
    if (data.is_superuser !== undefined) {
      setIsSuperuser(data.is_superuser);
      localStorage.setItem("isSuperuser", data.is_superuser ? "true" : "false");
    }
  }, []);

  const logoutUser = async () => {
    try {
      await api.post(`/LogoutUser/`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setAuthTokens(null);
      setAuth(false);
      setUsername(null);
      setFirstName(null);
      setLastName(null);
      setIsSuperuser(false);
      localStorage.clear();
      setLoading(false);
      navigate("/login", { replace: true });
    }
  };

  const loginUser = async (e, setIsLoading) => {
    e.preventDefault();
    const target = e.target;
    setIsLoading(true);

    try {
      const response = await api.post(
        `/token/`,
        {
          username: target.username.value,
          password: target.password.value,
        },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const data = response.data;
        setAuthTokens(data);
        const decoded = jwtDecode(data.access);

        updateUserData({
          username: decoded.name || decoded.username,
          first_name: decoded.first_name || "",
          last_name: decoded.last_name || "",
          is_superuser: decoded.is_superuser || false,
        });

        setAuth(true);
        localStorage.setItem("access_token", JSON.stringify(data));
        navigate("/", { replace: true });
      } else {
        toast.error("Неверные учетные данные");
      }
    } catch (error) {
      console.error("Login failed:", error);
      toast.error("Ошибка входа. Проверьте учетные данные");
    } finally {
      setIsLoading(false);
    }
  };

  const checkAuth = useCallback(async () => {

    try {
    
      
      const response = await api.post(
        `/token/refresh/`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        const data = response.data;
      
        
        setAuthTokens(data);
        setAuth(true);
        localStorage.setItem("access_token", JSON.stringify(data));
        const decoded = jwtDecode(data.access);

        updateUserData({
          username: decoded.name || decoded.username,
          first_name: decoded.first_name || "",
          last_name: decoded.last_name || "",
          is_superuser: decoded.is_superuser || false,
        });
        setLoading(false)
      }
    } catch (error) {
      console.log("Требуется повторная авторизация");
      localStorage.removeItem("access_token");
      localStorage.removeItem("username");
      localStorage.removeItem("first_name");
      localStorage.removeItem("last_name");
      localStorage.removeItem("isSuperuser");
      setAuth(false);
      setLoading(false)
    } 
  }, [updateUserData]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = {
    isAuthenticated,
    setAuth,
    loginUser,
    logoutUser,
    authTokens,
    loading,
    username,
    first_name,
    last_name,
    setUsername,
    isSuperuser,
    updateUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {" "}
      {loading ? <Loader /> :children}{" "}
    </AuthContext.Provider>
  );
};

export default AuthContext;